import type { ScenarioEdge, ScenarioStructure } from "../api-contracts/scenarios.js";

// Derivação PURA da estrutura de um cenário a
// partir de uma linha real. Antes existia só no cliente (`sandbox-draft.helpers.ts` +
// `SandboxCanvas/flow-helpers.ts`), o que fazia todo cenário nascer sem `structureSnap` e só ficar
// executável depois que alguém abrisse o sandbox e salvasse.
// Isso é um bloqueio: o botão Simular do hub precisa de um cenário executável no
// instante em que é criado. Extrair para cá é o que permite ao backend semear a estrutura sem
// reimplementar as regras — e o cliente passa a re-exportar daqui, com teste de paridade, para que
// as duas pontas não possam divergir.
// INVARIANTE: a estrutura semeada MUST ser idêntica à que o sandbox produziria no primeiro save.

/** Nível topológico (caminho mais longo a partir das raízes) por key de máquina. */
export function layeringOf(
  machineKeys: string[],
  edges: ReadonlyArray<{ sourceKey: string; targetKey: string }>
): Map<string, number> {
  const incoming = new Map<string, string[]>();
  const outgoing = new Map<string, string[]>();
  const keys = new Set(machineKeys);
  for (const k of machineKeys) {
    incoming.set(k, []);
    outgoing.set(k, []);
  }
  for (const e of edges) {
    if (!keys.has(e.sourceKey) || !keys.has(e.targetKey)) continue;
    outgoing.get(e.sourceKey)!.push(e.targetKey);
    incoming.get(e.targetKey)!.push(e.sourceKey);
  }
  // Kahn com nível = max(nível dos predecessores) + 1.
  const layer = new Map<string, number>();
  const indeg = new Map<string, number>();
  const queue: string[] = [];
  for (const k of machineKeys) {
    const d = incoming.get(k)!.length;
    indeg.set(k, d);
    if (d === 0) {
      layer.set(k, 0);
      queue.push(k);
    }
  }
  while (queue.length > 0) {
    const u = queue.shift()!;
    const lu = layer.get(u) ?? 0;
    for (const v of outgoing.get(u)!) {
      layer.set(v, Math.max(layer.get(v) ?? 0, lu + 1));
      indeg.set(v, (indeg.get(v) ?? 0) - 1);
      if ((indeg.get(v) ?? 0) === 0) queue.push(v);
    }
  }
  // Nós isolados / não alcançados caem no nível 0 (defensivo para ciclos residuais).
  for (const k of machineKeys) if (!layer.has(k)) layer.set(k, 0);
  return layer;
}

export interface DerivedStage {
  key: string;
  name: string;
  order: number;
}

/** Um estágio por nível ocupado, em ordem. Keys `stage-1..N`, `order` 0-based. */
export function stagesFromLayering(layer: Map<string, number>): {
  stages: DerivedStage[];
  stageKeyOf: Map<string, string>;
} {
  const levels = [...new Set([...layer.values()])].sort((a, b) => a - b);
  const keyByLevel = new Map<number, string>();
  const stages: DerivedStage[] = levels.map((lvl, i) => {
    const key = `stage-${i + 1}`;
    keyByLevel.set(lvl, key);
    return { key, name: `Estágio ${i + 1}`, order: i };
  });
  const stageKeyOf = new Map<string, string>();
  for (const [machineKey, lvl] of layer) stageKeyOf.set(machineKey, keyByLevel.get(lvl)!);
  return { stages, stageKeyOf };
}

/**
 * Edges máquina→máquina (todos-com-todos) entre estágios ATIVOS consecutivos.
 *
 * As edges do snapshot são sempre DERIVADAS do modelo em camadas, nunca copiadas do layout da
 * linha — START/END são âncoras de canvas e não participam do fluxo (convenção da).
 */
export function deriveEdgesFromStages(
  machines: ReadonlyArray<{ key: string; present: boolean; stageKey: string }>,
  stages: ReadonlyArray<DerivedStage>
): ScenarioEdge[] {
  const present = new Set(machines.filter((m) => m.present).map((m) => m.stageKey));
  const ordered = [...stages].filter((s) => present.has(s.key)).sort((a, b) => a.order - b.order);
  const inStage = (key: string) => machines.filter((m) => m.present && m.stageKey === key);

  const edges: ScenarioEdge[] = [];
  for (let i = 0; i < ordered.length - 1; i += 1) {
    const a = ordered[i];
    const b = ordered[i + 1];
    if (!a || !b) continue;
    for (const s of inStage(a.key)) {
      for (const t of inStage(b.key)) {
        edges.push({ sourceKey: s.key, targetKey: t.key, type: "PRODUCTION_FLOW" });
      }
    }
  }
  return edges;
}

export interface LineLayoutNodeInput {
  id: string;
  type: string;
  x: number;
  y: number;
  /** Extraído de `LineLayoutNode.data.machineId`; presente só em nós MACHINE. */
  machineId?: string | null;
}

export interface LineLayoutEdgeInput {
  sourceNodeId: string;
  targetNodeId: string;
  type: string;
}

export interface DeriveScenarioStructureInput {
  baseLineId: string;
  productId: string;
  /** Máquinas vivas da linha, na ordem em que a linha as lista. */
  machines: ReadonlyArray<{ id: string }>;
  nodes: ReadonlyArray<LineLayoutNodeInput>;
  edges: ReadonlyArray<LineLayoutEdgeInput>;
}

const DEFAULT_START = { key: "__start__", x: 0, y: 0 };
const DEFAULT_END = { key: "__end__", x: 600, y: 0 };

/**
 * Deriva o `structureSnap` de um cenário a partir da linha base.
 *
 * Regras (todas espelhadas do que o sandbox faz na hidratação — mudar aqui sem mudar lá quebra o
 * teste de paridade, que é o ponto):
 *  1. Só arestas `PRODUCTION_FLOW` entram; qualquer aresta tocando START/END é descartada.
 *  2. Estágios saem do layering por caminho mais longo.
 *  3. Toda máquina entra `present: true`, com `key = machineId` e `origin: "line"`.
 *  4. Posição vem do layout; sem nó correspondente, cai no fallback `x = 120 + i*160`.
 *  5. Linha sem topologia ⇒ um único estágio, com todas as máquinas em paralelo.
 */
export function deriveScenarioStructureFromLine(
  input: DeriveScenarioStructureInput
): ScenarioStructure {
  const machineByNode = new Map<string, string>();
  let start = { ...DEFAULT_START };
  let end = { ...DEFAULT_END };

  for (const n of input.nodes) {
    if (n.type === "MACHINE" && typeof n.machineId === "string") {
      machineByNode.set(n.id, n.machineId);
    } else if (n.type === "START") {
      start = { key: n.id, x: n.x, y: n.y };
    } else if (n.type === "END") {
      end = { key: n.id, x: n.x, y: n.y };
    }
  }

  const posByMachine = new Map<string, { x: number; y: number }>();
  for (const n of input.nodes) {
    const mid = machineByNode.get(n.id);
    if (mid) posByMachine.set(mid, { x: n.x, y: n.y });
  }

  const anchorKeys = new Set([start.key, end.key]);
  const nodeKey = (nodeId: string): string | null => {
    const mid = machineByNode.get(nodeId);
    if (mid) return mid;
    if (nodeId === start.key) return start.key;
    if (nodeId === end.key) return end.key;
    return null;
  };
  const lineEdges = input.edges
    .filter((e) => e.type === "PRODUCTION_FLOW")
    .map((e) => ({ sourceKey: nodeKey(e.sourceNodeId), targetKey: nodeKey(e.targetNodeId) }))
    .filter(
      (e): e is { sourceKey: string; targetKey: string } =>
        e.sourceKey != null &&
        e.targetKey != null &&
        !anchorKeys.has(e.sourceKey) &&
        !anchorKeys.has(e.targetKey)
    );

  const machineIds = input.machines.map((m) => m.id);
  const { stages, stageKeyOf } = stagesFromLayering(layeringOf(machineIds, lineEdges));

  const machines = input.machines.map((m) => ({
    key: m.id,
    sourceMachineId: m.id,
    origin: "line" as const,
    present: true,
    stageKey: stageKeyOf.get(m.id) ?? stages[0]?.key ?? "stage-1",
  }));

  const canvasNodes = [
    ...machines.map((m, i) => {
      const pos = posByMachine.get(m.key) ?? { x: 120 + i * 160, y: 0 };
      return { key: m.key, x: pos.x, y: pos.y, nodeType: "MACHINE" as const };
    }),
    { key: start.key, x: start.x, y: start.y, nodeType: "START" as const },
    { key: end.key, x: end.x, y: end.y, nodeType: "END" as const },
  ];

  return {
    machines,
    stages,
    edges: deriveEdgesFromStages(machines, stages),
    canvas: { nodes: canvasNodes },
    productId: input.productId,
    baseLineId: input.baseLineId,
  };
}
